import React, { useLayoutEffect, useRef } from "react";
import { Lightbulb, Plus, X } from "lucide-react";
import {
  Section,
  SectionHeader,
  SectionTitleGroup,
  SectionTitle,
  SectionAction,
  KeyAreaRow,
  KeyAreaRows,
  KeyAreaText,
  KeyAreaRemoveBtn,
  KeyAreaInput,
} from "../../organisms/StoryBuilder/StoryBuilder.styles";
import { KeyAreasSkeleton } from "../../organisms/StoryBuilder/StoryBuilder.skeleton";
import {
  CannotAnswer,
  CannotAnswerTitle,
  KeyAreaBody,
  KeyAreaDetail,
  KeyAreasSectionBody,
} from "./StoryKeyAreasSection.styles";
import type { StoryKeyAreasSectionProps } from "./StoryKeyAreasSection.types";

export const StoryKeyAreasSection: React.FC<StoryKeyAreasSectionProps> = (
  props,
) => {
  const {
    className,
    title = "Key areas",
    keyAreas,
    isPending = false,
    keyAreaInput,
    onKeyAreaInputChange,
    onKeyAreaKeyDown,
    onRemoveKeyArea,
    disabled = false,
    scrollable = false,
    placeholder = "Type to add more key areas of analysis...",
    cannotAnswer = [],
  } = props;
  const rowsRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const previousKeyAreaCountRef = useRef(keyAreas.length);

  useLayoutEffect(() => {
    const previousCount = previousKeyAreaCountRef.current;
    previousKeyAreaCountRef.current = keyAreas.length;
    if (!scrollable || keyAreas.length <= previousCount || !rowsRef.current) {
      return;
    }
    rowsRef.current.scrollTop = rowsRef.current.scrollHeight;
  }, [keyAreas.length, scrollable]);

  return (
    <Section className={className} data-testid="section-key-areas">
      <SectionHeader>
        <SectionTitleGroup>
          <Lightbulb size={16} />
          <SectionTitle>{title}</SectionTitle>
        </SectionTitleGroup>
        {!isPending && (
          <SectionAction
            type="button"
            disabled={disabled}
            aria-label="Add key area"
            onClick={() => inputRef.current?.focus()}
          >
            <Plus size={12} />
            Add
          </SectionAction>
        )}
      </SectionHeader>
      <KeyAreasSectionBody>
        {isPending ? (
          <KeyAreasSkeleton />
        ) : (
          <>
            {keyAreas.length > 0 ? (
              <KeyAreaRows
                ref={rowsRef}
                $scrollable={scrollable}
                data-testid="key-area-rows"
              >
                {keyAreas.map((area, index) => (
                  <KeyAreaRow key={area.id}>
                    <KeyAreaBody>
                      <KeyAreaText>{index + 1}.&nbsp;{area.text}</KeyAreaText>
                      {area.details?.map((detail) => (
                        <KeyAreaDetail key={detail}>{detail}</KeyAreaDetail>
                      ))}
                    </KeyAreaBody>
                    <KeyAreaRemoveBtn
                      type="button"
                      aria-label={`Remove key area ${index + 1}`}
                      disabled={disabled}
                      onClick={() => onRemoveKeyArea?.(area.id)}
                    >
                      <X size={16} />
                    </KeyAreaRemoveBtn>
                  </KeyAreaRow>
                ))}
              </KeyAreaRows>
            ) : null}
            {cannotAnswer.length > 0 ? (
              <CannotAnswer data-testid="cannot-answer">
                <CannotAnswerTitle>What the plan can&apos;t answer</CannotAnswerTitle>
                {cannotAnswer.map((line) => (
                  <KeyAreaDetail key={line}>{line}</KeyAreaDetail>
                ))}
              </CannotAnswer>
            ) : null}
            <KeyAreaInput
              ref={inputRef}
              value={keyAreaInput}
              onChange={(e) => onKeyAreaInputChange(e.target.value)}
              onKeyDown={onKeyAreaKeyDown}
              disabled={disabled}
              placeholder={placeholder}
              aria-label="Add key area of analysis"
            />
          </>
        )}
      </KeyAreasSectionBody>
    </Section>
  );
};

export default StoryKeyAreasSection;
